/**
 * ResetPasswordPage Component
 *
 * Public (pre-auth) page for setting a new password from an emailed link.
 * Serves BOTH flows on the same token machinery:
 * - password reset  (?token= from a "forgot password" / admin-reset email)
 * - invitation      (?token= from an invite email — welcome copy variant)
 *
 * Security: the token is read once from the URL and immediately stripped
 * from the address bar (history.replaceState) so it never lingers in
 * browser history / shared screenshots. Validation and consumption both
 * send it in the POST body only.
 */

import { useEffect, useRef, useState, FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LoadingOutlined,
  LockOutlined,
} from '@ant-design/icons';
import authService from '@modules/auth/services/authService';
import { useBrand, useBrandAssets } from '@/brand';
import { useLanguage } from '@/hooks/useLanguage';
import { checkPasswordRules, validatePasswordPolicy } from '@/utils/passwordPolicy';
import './LoginPage.css';

type PageState =
  | { phase: 'validating' }
  | { phase: 'form'; purpose: 'reset' | 'invite'; emailMasked: string; tenantCode: string }
  | { phase: 'success'; tenantCode: string }
  | { phase: 'invalid' };

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const brand = useBrand();
  const { logo: loginLogo } = useBrandAssets();
  const { t } = useLanguage();

  // Token captured once on mount, then wiped from the URL.
  const tokenRef = useRef<string | null>(null);
  const [state, setState] = useState<PageState>({ phase: 'validating' });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // StrictMode double-invokes effects in dev: the first run strips the
    // token from the URL, so the second run must reuse the captured ref
    // instead of reading an (intentionally) empty query string.
    const token = params.get('token') || tokenRef.current;
    tokenRef.current = token;

    // Strip the token from the address bar immediately.
    if (params.get('token')) {
      params.delete('token');
      const query = params.toString();
      window.history.replaceState(
        {}, '', window.location.pathname + (query ? `?${query}` : ''),
      );
    }

    if (!token) {
      setState({ phase: 'invalid' });
      return;
    }

    let cancelled = false;
    authService.validatePasswordToken(token).then((result) => {
      if (cancelled) return;
      if (result.valid && result.purpose) {
        setState({
          phase: 'form',
          purpose: result.purpose,
          emailMasked: result.email_masked ?? '',
          tenantCode: result.tenant_code ?? tenantSlug ?? '',
        });
      } else {
        setState({ phase: 'invalid' });
      }
    }).catch(() => {
      if (!cancelled) setState({ phase: 'invalid' });
    });
    return () => { cancelled = true; };
  }, [tenantSlug]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (state.phase !== 'form' || !tokenRef.current) return;

    setError('');
    const policyError = validatePasswordPolicy(newPassword);
    if (policyError) {
      setError(t(policyError.key, { ...policyError.params, defaultValue: policyError.defaultValue }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.reset.errorMismatch', { defaultValue: 'Passwords do not match' }));
      return;
    }

    setSubmitting(true);
    try {
      const { tenant_code } = await authService.resetPasswordWithToken(tokenRef.current, newPassword);
      setState({ phase: 'success', tenantCode: tenant_code || state.tenantCode });
    } catch (err: any) {
      // A consumed/expired token can no longer be retried — flip to the
      // invalid state instead of leaving a dead form on screen.
      setError(err.message || t('auth.reset.invalidBody', { defaultValue: 'This link is invalid or has expired.' }));
      setState({ phase: 'invalid' });
    } finally {
      setSubmitting(false);
    }
  };

  const loginPath = `/${(state.phase === 'form' || state.phase === 'success' ? state.tenantCode : '') || tenantSlug || ''}/login`;

  const renderBody = () => {
    switch (state.phase) {
      case 'validating':
        return (
          <div className="login-form" style={{ textAlign: 'center', padding: '32px 0' }}>
            <LoadingOutlined style={{ fontSize: 28 }} />
          </div>
        );

      case 'invalid':
        return (
          <div className="login-form">
            <div className="error-message">
              <ExclamationCircleOutlined className="error-icon" />
              <span dir="auto" className="error-text">
                {t('auth.reset.invalidBody', { defaultValue: 'This link is invalid or has expired.' })}
              </span>
            </div>
            <Link to={loginPath} className="login-btn" style={{ textAlign: 'center', textDecoration: 'none', display: 'block', lineHeight: '44px' }}>
              {t('auth.reset.requestNew', { defaultValue: 'Back to sign in' })}
            </Link>
          </div>
        );

      case 'success':
        return (
          <div className="login-form">
            <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
              <CheckCircleOutlined style={{ fontSize: 40, color: 'var(--success, #22c55e)' }} />
              <p style={{ marginTop: 12 }}>
                {t('auth.reset.successBody', { defaultValue: 'Your password has been set. You can now sign in with it.' })}
              </p>
            </div>
            <button type="button" className="login-btn" onClick={() => navigate(loginPath)}>
              {t('auth.reset.goToLogin', { defaultValue: 'Go to sign in' })}
            </button>
          </div>
        );

      case 'form':
        return (
          <form className="login-form" onSubmit={handleSubmit}>
            {error && (
              <div className="error-message">
                <ExclamationCircleOutlined className="error-icon" />
                <span dir="auto" className="error-text">{error}</span>
              </div>
            )}

            {state.emailMasked && (
              <p style={{ marginBottom: 16, color: 'var(--text-secondary, #6b7280)' }}>
                {t('auth.reset.emailHint', { defaultValue: 'Setting a password for' })}{' '}
                <strong>{state.emailMasked}</strong>
              </p>
            )}

            <div className="form-group">
              <label htmlFor="new-password" className="form-label">
                {t('auth.reset.newPassword', { defaultValue: 'New password' })}
              </label>
              <div className="input-row">
                <div className="input-wrapper">
                  <input dir="auto"
                    type={showPassword ? 'text' : 'password'}
                    id="new-password"
                    className="form-input has-toggle"
                    placeholder={t('auth.reset.newPasswordPlaceholder', { defaultValue: 'At least 8 characters' })}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={t('login.togglePasswordVisibility')}
                  >
                    {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  </button>
                </div>
              </div>
              {/* Live requirements checklist — turns green as each rule is met. */}
              <ul className="password-rules" aria-label={t('passwordPolicy.title', { defaultValue: 'Password requirements' })}>
                {checkPasswordRules(newPassword).map((rule) => (
                  <li key={rule.key} className={rule.met ? 'rule-met' : 'rule-unmet'}>
                    <span aria-hidden="true">{rule.met ? '✓' : '○'}</span>{' '}
                    {t(rule.key, { ...rule.params, defaultValue: rule.label })}
                  </li>
                ))}
              </ul>
            </div>

            <div className="form-group">
              <label htmlFor="confirm-password" className="form-label">
                {t('auth.reset.confirmPassword', { defaultValue: 'Confirm password' })}
              </label>
              <div className="input-row">
                <div className="input-wrapper">
                  <input dir="auto"
                    type={showConfirm ? 'text' : 'password'}
                    id="confirm-password"
                    className="form-input has-toggle"
                    placeholder={t('auth.reset.confirmPasswordPlaceholder', { defaultValue: 'Repeat the new password' })}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirm(!showConfirm)}
                    aria-label={t('login.togglePasswordVisibility')}
                  >
                    {showConfirm ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  </button>
                </div>
              </div>
            </div>

            <button type="submit" className="login-btn" disabled={submitting}>
              {submitting ? (
                <>
                  <LoadingOutlined className="btn-spinner" />
                  <span>{t('auth.reset.submitting', { defaultValue: 'Saving…' })}</span>
                </>
              ) : (
                <span>{t('auth.reset.submit', { defaultValue: 'Set password' })}</span>
              )}
            </button>
          </form>
        );
    }
  };

  const isInvite = state.phase === 'form' && state.purpose === 'invite';
  // An invitation's heading is two lines, two keys — "Welcome", then "Set your
  // password" under it (Segev, 2026-11-04: no dash joining them).
  const title = isInvite
    ? t('auth.reset.welcomeTitle', { defaultValue: 'Welcome' })
    : state.phase === 'success'
      ? t('auth.reset.successTitle', { defaultValue: 'Password set' })
      : state.phase === 'invalid'
        ? t('auth.reset.invalidTitle', { defaultValue: 'Link expired' })
        : t('auth.reset.title', { defaultValue: 'Reset your password' });

  return (
    <div className="login-page">
      {/* Simplified branding panel — same surface as the login page,
          without the animated frame. */}
      <div className="login-branding">
        <div className="branding-content">
          <img src={loginLogo} alt={brand.name} className="branding-logo" />
          <p className="branding-eyebrow" dir="auto">{brand.loginEyebrow ?? brand.name}</p>
          <h2 className="branding-tagline" dir="auto">{t(brand.tagline, { defaultValue: brand.tagline })}</h2>
        </div>
      </div>

      <div className="login-form-panel">
        <div className="form-container">
          <div className="form-header">
            <h1 className="form-title" style={{ marginBottom: 4 }}>
              <LockOutlined style={{ marginInlineEnd: 8 }} />
              {title}
            </h1>
            {isInvite && (
              <p className="form-subtitle">{t('auth.reset.welcomeSubtitle', { defaultValue: 'Set your password' })}</p>
            )}
          </div>
          {renderBody()}
          <div className="form-footer">
            <div className="legal-links">
              <Link to={loginPath}>{t('auth.reset.backToLogin', { defaultValue: 'Back to sign in' })}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
